import { API_ENDPOINTS } from "@/api"
import type {
  AWSCredentialsCheckStatusResponse,
  AWSCredentialsRemoveResponse,
  AWSCredentialsSetupRequest,
  AWSCredentialsSetupResponse,
  AWSRoleConnectResponse,
  AWSRoleSetupResponse,
  AWSSetupFormData,
} from "@/types/aws.types"
import axios from "axios"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001/"

/**
 * AWS Service
 *
 * Handles AWS account setup and credentials management including:
 * - AWS credentials setup
 * - Account connection
 * - Credential validation
 */
export class AWSService {
  /**
   * Format API error response into user-friendly message
   * @param errorData Error data from API response
   */
  private formatErrorMessage(errorData: any): string {
    // If error data is a string, return it directly
    if (typeof errorData === "string") {
      return errorData
    }

    // If error data is an object
    if (typeof errorData === "object" && errorData !== null) {
      // Handle { message, detail, error } shape returned by the API
      if (typeof errorData.message === "string") return errorData.message
      if (typeof errorData.detail === "string") return errorData.detail

      // Handle field-specific validation errors: { field: ["msg"] }
      const errors: string[] = []
      for (const [field, messages] of Object.entries(errorData)) {
        if (Array.isArray(messages) && messages.length > 0) {
          const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          errors.push(`${formattedField}: ${messages[0]}`)
        }
      }
      if (errors.length > 0) return errors.join("\n")
    }

    return "AWS setup failed. Please try again."
  }

  /**
   * Transform AWS form data to API request format
   * @param formData AWS setup form data from UI
   */
  private convertToPayload(formData: AWSSetupFormData): AWSCredentialsSetupRequest {
    return {
      access_key_id: formData.accessKeyId,
      secret_access_key: formData.secretAccessKey,
    }
  }

  /**
   * Setup AWS credentials
   * Endpoint: POST /v2/account/add-aws-credentials/
   * @param formData AWS setup form data
   */
  async setupAWSCredentials(formData: AWSSetupFormData): Promise<AWSCredentialsSetupResponse> {
    try {
      const requestData = this.convertToPayload(formData)
      // Use axios directly for v2 endpoint with token from localStorage
      const response = await axios.post<AWSCredentialsSetupResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.account.awsCredentials}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      )
      return response.data
    } catch (error: any) {
      // Format error message from API response
      // API returns errors in format: { "field_name": ["error message"] }
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)

      // Throw error with formatted message
      const apiError = new Error(message)
      // Attach original error data for field-level error handling if needed
      ;(apiError as any).fieldErrors = errorData
      throw apiError
    }
  }

  /**
   * Re-check already-stored AWS credentials against AWS.
   * Unlike setupAWSCredentials, this does not require re-entering the keys —
   * it re-validates whatever is already stored and persists the real outcome.
   * Endpoint: POST /v2/account/aws-credentials/check-status/
   */
  async checkAWSCredentialsStatus(): Promise<AWSCredentialsCheckStatusResponse> {
    try {
      const response = await axios.post<AWSCredentialsCheckStatusResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.account.checkAwsCredentialsStatus}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      )
      return response.data
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      throw new Error(message)
    }
  }

  /**
   * Remove AWS credentials
   * Endpoint: DELETE /v2/account/remove-aws-credentials/
   */
  async removeAWSCredentials(): Promise<AWSCredentialsRemoveResponse> {
    try {
      const response = await axios.delete<AWSCredentialsRemoveResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.account.removeAwsCredentials}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      )
      return response.data
    } catch (error: any) {
      const errorData = error.response?.data
      const message = this.formatErrorMessage(errorData)
      throw new Error(message)
    }
  }

  /**
   * Start IAM role setup: returns the External ID, CloudSpry's AWS account ID and the trust policy.
   * Endpoint: POST /v2/account/setup-aws-role/
   */
  async setupAWSRole(): Promise<AWSRoleSetupResponse> {
    try {
      const response = await axios.post<AWSRoleSetupResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.account.setupAwsRole}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      )
      return response.data
    } catch (error) {
      const errorData = (error as { response?: { data?: unknown } }).response?.data
      throw new Error(this.formatErrorMessage(errorData))
    }
  }

  /**
   * Connect an AWS account through an IAM role ARN.
   * Endpoint: POST /v2/account/connect-aws-role/
   * @param roleArn The role's ARN, e.g. arn:aws:iam::123456789012:role/RoleName
   */
  async connectAWSRole(roleArn: string): Promise<AWSRoleConnectResponse> {
    try {
      const response = await axios.post<AWSRoleConnectResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.account.connectAwsRole}`,
        { role_arn: roleArn },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
          timeout: 20000,
        }
      )
      return response.data
    } catch (error) {
      const errorData = (error as { response?: { data?: unknown } }).response?.data
      throw new Error(this.formatErrorMessage(errorData))
    }
  }
}

/**
 * Singleton instance of AWSService
 */
export const awsService = new AWSService()

/**
 * Setup AWS credentials
 * @param formData AWS setup form data
 */
export const setupAWSCredentials = async (formData: AWSSetupFormData): Promise<AWSCredentialsSetupResponse> => {
  return awsService.setupAWSCredentials(formData)
}

/**
 * Remove AWS credentials
 */
export const removeAWSCredentials = async (): Promise<AWSCredentialsRemoveResponse> => {
  return awsService.removeAWSCredentials()
}

/**
 * Start IAM role setup (External ID + trust policy)
 */
export const setupAWSRole = async (): Promise<AWSRoleSetupResponse> => {
  return awsService.setupAWSRole()
}

/**
 * Connect an AWS account through an IAM role ARN
 */
export const connectAWSRole = async (roleArn: string): Promise<AWSRoleConnectResponse> => {
  return awsService.connectAWSRole(roleArn)
}

/**
 * Re-check already-stored AWS credentials against AWS (no re-entry required)
 */
export const checkAWSCredentialsStatus = async (): Promise<AWSCredentialsCheckStatusResponse> => {
  return awsService.checkAWSCredentialsStatus()
}
